import { Profile } from '../types/profile';

// Get the base URL - use localhost for development
const API_URL = import.meta.env.DEV 
  ? 'http://localhost:3001/api'
  : `${window.location.protocol}//${window.location.host}/api`;

class AuthService {
  private token: string | null = null;
  private cachedUser: any = null;

  constructor() {
    this.token = localStorage.getItem('token');
    try {
      const cachedUserData = localStorage.getItem('saycard_user_data');
      if (cachedUserData) {
        this.cachedUser = JSON.parse(cachedUserData);
      }
    } catch (e) {
      console.error('Failed to parse cached user data:', e);
    }
  }

  setToken(token: string | null) {
    this.token = token;
    if (token) {
      localStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
      localStorage.removeItem('saycard_user_data');
      this.cachedUser = null;
    }
  }

  setCachedUser(user: any) {
    // Store the main user data
    this.cachedUser = user;
    
    if (user) {
      // Get existing country if any
      const existingData = localStorage.getItem('saycard_user_data');
      const existingUser = existingData ? JSON.parse(existingData) : {};
      const country = user.country || existingUser.country;
      
      // Store user data with preserved country
      const userData = {
        ...user,
        country // Only preserve country, rest comes from server
      };
      
      localStorage.setItem('saycard_user_data', JSON.stringify(userData));
    } else {
      localStorage.removeItem('saycard_user_data');
    }
  }

  getCachedUser() {
    return this.cachedUser;
  }

  private async request(endpoint: string, options: RequestInit = {}) {
    const url = `${API_URL}${endpoint}`;
    const headers = new Headers({
      'Content-Type': 'application/json',
    });

    // Get the latest token before making the request
    const token = this.getToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    const finalOptions: RequestInit = {
      ...options,
      headers,
      credentials: 'include' as RequestCredentials,
    };

    const response = await fetch(url, finalOptions);
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Request failed');
    }

    return response.json();
  }

  async login(email: string, password: string) {
    const response = await this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    if (response?.user) {
      this.setCachedUser(response.user);
    }
    return response;
  }

  async register(email: string, password: string) {
    return this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async googleAuth(credential: string) {
    const response = await this.request('/auth/google', {
      method: 'POST',
      body: JSON.stringify({ credential }),
    });
    if (response?.user) {
      this.setCachedUser(response.user);
    }
    return response;
  }

  async createProfile(data: Partial<Profile>) {
    return this.request('/profiles', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateProfile(data: Partial<Profile>) {
    return this.request(`/profiles/${data._id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteProfile(profileId: string) {
    return this.request(`/profiles/${profileId}`, {
      method: 'DELETE',
    });
  }

  async getSession() {
    try {
      const response = await this.request('/auth/session');
      if (response?.user) {
        this.setCachedUser(response.user);
      }
      return response;
    } catch (error) {
      // If session request fails, return cached user if available
      if (this.cachedUser) {
        return { user: this.cachedUser };
      }
      throw error;
    }
  }

  async getProfiles() {
    return this.request('/profiles');
  }

  async getProfile(profileId: string) {
    const response = await this.request('/auth/profile');
    if (response?.profiles) {
      const profile = response.profiles.find((p: any) => p._id === profileId);
      if (!profile) {
        throw new Error('Profile not found');
      }
      return { data: profile };
    }
    throw new Error('No profiles returned');
  }

  async updateUser(data: { name?: string; country?: string }) {
    const response = await this.request('/auth/user', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    if (response?.user) {
      this.setCachedUser(response.user);
    }
    return response;
  }

  async updatePassword(currentPassword: string | undefined, newPassword: string) {
    return this.request('/auth/password', {
      method: 'PUT',
      body: JSON.stringify({
        currentPassword,
        newPassword
      })
    });
  }

  async logout() {
    this.setToken(null);
    this.setCachedUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('lastUsedProfileId');
    localStorage.removeItem('saycard_user_data');
  }

  getToken(): string | null {
    // Always get the latest token from localStorage
    this.token = localStorage.getItem('token');
    return this.token;
  }
}

export default new AuthService();
